import { VideoContent } from '../../../types/content';
import { LiveVideoService, GetCompletedVideosOptions, LiveSearchParams } from '../../../types/lives';
import { ApiResponse, API_STATUS_CODES } from '../../../types/api';
import { env } from '../../../config/env';
import { get } from '../../../utils/api';

export class APIProvider implements LiveVideoService {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = env.apiBaseUrl;
  }

  private async makeRequest<T>(endpoint: string, params?: Record<string, string | number | boolean | string[] | undefined>): Promise<ApiResponse<T>> {
    try {
      // Use the centralized fetch wrapper
      return await get<ApiResponse<T>>(endpoint, params, {
        validateApiResponse: false, // We want the raw ApiResponse
        baseUrl: this.baseUrl
      });
    } catch (error) {
      // Re-throw the error - it's already been logged by the fetch wrapper
      throw error;
    }
  }

  public async getCompletedVideos(options: GetCompletedVideosOptions = { broadcastStatus: 'completed' }): Promise<ApiResponse<VideoContent>> {
    const { page = 1, limit = 10, sortBy = 'createdAt', sortType = 'desc' } = options;
    
    const response = await this.makeRequest<VideoContent>('/lives', {
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortType,
      broadcastStatus: 'completed'
    });

    return response;
  }

  public async searchLives(params: LiveSearchParams): Promise<ApiResponse<VideoContent>> {
    const queryParams = {
      page: (params.page || 1).toString(),
      limit: (params.limit || 10).toString(),
      ...(params.broadcastStatus && { broadcastStatus: params.broadcastStatus })
    };

    const response = await this.makeRequest<VideoContent>('/lives', queryParams);
    return response;
  }

  public async getLiveById(id: string): Promise<VideoContent> {
    const response = await this.makeRequest<VideoContent>(`/lives/${id}`);
    
    if (!response.data) {
      throw new Error('Live video not found');
    }

    if ('results' in response.data) {
      const results = response.data.results;
      if (!results?.length) {
        throw new Error('Live video not found');
      }
      return results[0];
    }

    return response.data as VideoContent;
  }

  public async getLiveVideos(): Promise<VideoContent | null> {
    try {
      const response = await this.makeRequest<VideoContent>('/lives', {
        broadcastStatus: 'live',
        limit: '1'
      });
      
      if (!response.data || !('results' in response.data)) {
        return null;
      }
      
      return response.data.results[0] || null;
    } catch (error) {
      console.error('Failed to fetch live videos:', error);
      return null;
    }
  }
}